import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import * as api from "../lib/api";
import { getRankIcon, isErRank } from "../lib/ranks";
import "./MyKabile.css";

interface KabileMember {
  id: string;
  username: string;
  avatarUrl?: string;
  savwarTP: number;
  savwarRank: number;
  joinedAt: Date;
  role: 'leader' | 'member';
}

interface PendingRequest {
  id: string;
  userId: string;
  username: string;
  avatarUrl?: string;
  savwarTP: number;
  savwarRank: number;
  createdAt: Date;
}

interface Kabile {
  id: string;
  name: string;
  description: string;
  flagUrl?: string;
  leaderId: string;
  founderId?: string;
  maxMembers: number;
  minTP: number;
  createdAt: Date;
  members: KabileMember[];
  totalTP: number;
  isLeader?: boolean;
  pendingRequests?: PendingRequest[];
}

export default function MyKabile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kabile, setKabile] = useState<Kabile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    description: '',
    maxMembers: 10,
    minTP: 0,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string>("");
  const [flagImage, setFlagImage] = useState<File | null>(null);
  const [flagPreview, setFlagPreview] = useState<string>("");
  const [isUploadingFlag, setIsUploadingFlag] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImage, setTempImage] = useState<string>("");
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 400, height: 300 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Davet modalı state'leri
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [followingUsers, setFollowingUsers] = useState<any[]>([]);
  const [isLoadingFollowing, setIsLoadingFollowing] = useState(false);
  const [inviteError, setInviteError] = useState<string>("");
  const [invitingUserId, setInvitingUserId] = useState<string | null>(null);

  useEffect(() => {
    loadKabile();
  }, []);

  const loadKabile = async () => {
    try {
      const data = await api.getMyKabile();
      
      // Convert relative URLs to absolute for local development
      if (data && data.flagUrl && data.flagUrl.startsWith('/uploads/')) {
        const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:5003';
        data.flagUrl = apiBase + data.flagUrl;
      }
      
      setKabile(data);
    } catch (error) {
      console.error("Kabile yüklenemedi:", error);
      setKabile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    if (!kabile) return;
    setSettingsForm({
      description: kabile.description || '',
      maxMembers: kabile.maxMembers,
      minTP: kabile.minTP,
    });
    setFlagImage(null);
    setFlagPreview(kabile.flagUrl || "");
    setSettingsError("");
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    if (!kabile) return;
    
    setSettingsError("");
    
    // Validation
    if (settingsForm.description.length > 200) {
      setSettingsError("Açıklama en fazla 200 karakter olabilir");
      return;
    }
    
    if (settingsForm.maxMembers < kabile.members.length) {
      setSettingsError(`Maksimum üye sayısı mevcut üye sayısından (${kabile.members.length}) az olamaz`);
      return;
    }
    
    if (settingsForm.maxMembers < 5 || settingsForm.maxMembers > 100) {
      setSettingsError("Maksimum üye sayısı 5-100 arasında olmalı");
      return;
    }
    
    if (settingsForm.minTP < 0) {
      setSettingsError("Minimum TP 0'dan küçük olamaz");
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Update kabile settings
      await api.updateKabile(kabile.id, settingsForm);
      
      // Upload new flag if selected
      if (flagImage) {
        setIsUploadingFlag(true);
        const flagFormData = new FormData();
        flagFormData.append('flag', flagImage);
        await api.uploadKabileFlag(kabile.id, flagFormData);
      }
      
      // Kabile verisini yeniden yükle (bayrak URL'sini al)
      await loadKabile();
      
      // Modalı kapat ve state'leri temizle
      setShowSettingsModal(false);
      setFlagImage(null);
      setFlagPreview("");
    } catch (error: any) {
      setSettingsError(error.message || "Ayarlar güncellenirken bir hata oluştu");
    } finally {
      setIsSaving(false);
      setIsUploadingFlag(false);
    }
  };

  const handleLeaveKabile = async () => {
    if (!confirm("Kabileden ayrılmak istediğinize emin misiniz?")) return;
    
    try {
      await api.leaveKabile();
      navigate("/savkabile");
    } catch (error) {
      alert("Kabileden ayrılırken bir hata oluştu");
    }
  };

  const handleDeleteKabile = async () => {
    if (!confirm("Kabileyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    
    if (!kabile) return;
    
    try {
      await api.deleteKabile(kabile.id);
      navigate("/savkabile");
    } catch (error) {
      alert("Kabile silinirken bir hata oluştu");
    }
  };

  const handleKickMember = async (memberId: string, memberUsername: string) => {
    if (!confirm(`${memberUsername} kullanıcısını kabileden çıkarmak istediğinize emin misiniz?`)) return;
    
    if (!kabile) return;
    
    try {
      await api.kickKabileMember(kabile.id, memberId);
      // Refresh kabile data
      await loadKabile();
    } catch (error) {
      alert("Üye çıkarılırken bir hata oluştu");
    }
  };

  const handleApproveRequest = async (notificationId: string) => {
    try {
      await api.approveJoinRequest(notificationId);
      alert("Kullanıcı kabileye eklendi!");
      await loadKabile(); // Refresh to show new member and remove from pending
    } catch (error: any) {
      alert(error.message || "Talep onaylanırken bir hata oluştu");
    }
  };

  const handleRejectRequest = async (notificationId: string) => {
    try {
      await api.rejectJoinRequest(notificationId);
      await loadKabile(); // Refresh to remove from pending list
    } catch (error: any) {
      alert(error.message || "Talep reddedilirken bir hata oluştu");
    }
  };

  const handleFlagUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setSettingsError('Lütfen bir resim dosyası seçin');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setSettingsError('Dosya boyutu 2MB\'dan küçük olmalı');
      return;
    }

    setSettingsError('');

    // Show crop modal
    const reader = new FileReader();
    reader.onloadend = () => {
      setTempImage(reader.result as string);
      setShowCropModal(true);
      // Reset crop area - ortada başlat
      setCropArea({ x: 200, y: 150, width: 400, height: 300 });
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = () => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return;
      
      // Container boyutları
      const containerWidth = 800;
      const containerHeight = 600;
      
      // Gerçek resim boyutları
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;
      
      // object-fit: contain ile resmin container'daki görünen boyutlarını hesapla
      const imgAspect = imgWidth / imgHeight;
      const containerAspect = containerWidth / containerHeight;
      
      let displayWidth, displayHeight, offsetX, offsetY;
      
      if (imgAspect > containerAspect) {
        // Resim yatay olarak daha geniş - genişliğe göre ölçekle
        displayWidth = containerWidth;
        displayHeight = containerWidth / imgAspect;
        offsetX = 0;
        offsetY = (containerHeight - displayHeight) / 2;
      } else {
        // Resim dikey olarak daha uzun - yüksekliğe göre ölçekle
        displayHeight = containerHeight;
        displayWidth = containerHeight * imgAspect;
        offsetX = (containerWidth - displayWidth) / 2;
        offsetY = 0;
      }
      
      // Crop koordinatlarını gerçek resim koordinatlarına çevir
      const scaleX = imgWidth / displayWidth;
      const scaleY = imgHeight / displayHeight;
      
      // Negatif değerleri sıfırla ve resim sınırlarını kontrol et
      let sourceX = (cropArea.x - offsetX) * scaleX;
      let sourceY = (cropArea.y - offsetY) * scaleY;
      let sourceWidth = cropArea.width * scaleX;
      let sourceHeight = cropArea.height * scaleY;
      
      // Sınır kontrolü
      sourceX = Math.max(0, sourceX);
      sourceY = Math.max(0, sourceY);
      sourceWidth = Math.min(sourceWidth, imgWidth - sourceX);
      sourceHeight = Math.min(sourceHeight, imgHeight - sourceY);
      
      console.log('📏 Resim boyutları:', { imgWidth, imgHeight });
      console.log('📏 Display boyutları:', { displayWidth, displayHeight, offsetX, offsetY });
      console.log('📏 Crop area:', cropArea);
      console.log('📏 Source (düzeltilmiş):', { sourceX, sourceY, sourceWidth, sourceHeight });
      
      // Canvas'ı source boyutunda yap (aspect ratio korusun)
      canvas.width = sourceWidth;
      canvas.height = sourceHeight;
      
      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, sourceWidth, sourceHeight // Canvas'ı tam doldur
      );
      
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedFile = new File([blob], 'kabile-flag.png', { type: 'image/png' });
          setFlagImage(croppedFile);
          console.log('✅ Cropped flag image saved:', croppedFile);
          console.log('📐 Crop coordinates:', { sourceX, sourceY, sourceWidth, sourceHeight });
          
          setFlagPreview(canvas.toDataURL('image/png'));
          setShowCropModal(false);
          setTempImage("");
        }
      }, 'image/png');
    };
    img.src = tempImage;
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setTempImage("");
    const fileInput = document.getElementById('flag-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - cropArea.x, y: e.clientY - cropArea.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    // Container boyutları
    const containerWidth = 800;
    const containerHeight = 600;
    
    // Yeni pozisyonu hesapla
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Crop box'ı container sınırları içinde tut
    const clampedX = Math.max(0, Math.min(newX, containerWidth - cropArea.width));
    const clampedY = Math.max(0, Math.min(newY, containerHeight - cropArea.height));
    
    setCropArea({ ...cropArea, x: clampedX, y: clampedY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleOpenInviteModal = async () => {
    if (!kabile || !user) return;
    
    setShowInviteModal(true);
    setInviteError("");
    setIsLoadingFollowing(true);
    
    try {
      // Get following users
      const following = await api.getFollowing();
      
      // Filter out users who are already in the kabile
      const memberIds = new Set(kabile.members.map(m => m.id));
      const availableUsers = following.filter(f => !memberIds.has(f.id));
      
      setFollowingUsers(availableUsers);
    } catch (error) {
      setInviteError("Takip listesi yüklenirken bir hata oluştu");
    } finally {
      setIsLoadingFollowing(false);
    }
  };

  const handleInviteUser = async (invitedUserId: string) => {
    if (!kabile) return;
    
    setInvitingUserId(invitedUserId);
    setInviteError("");
    
    try {
      await api.inviteToKabile(kabile.id, invitedUserId);
      
      // Remove invited user from list
      setFollowingUsers(prev => prev.filter(u => u.id !== invitedUserId));
      
      if (followingUsers.length <= 1) {
        setShowInviteModal(false);
      }
    } catch (error: any) {
      setInviteError(error.message || "Davet gönderilirken bir hata oluştu");
    } finally {
      setInvitingUserId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="my-kabile-container">
        <div className="kabile-loading">
          <div className="loading-spinner"></div>
          <p>Kabile bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!kabile) {
    return (
      <div className="my-kabile-container">
        <div className="no-kabile">
          <h2>🏛️ Henüz Bir Kabileye Üye Değilsiniz</h2>
          <p>Kendi kabilenizi oluşturun veya bir kabileye katılın</p>
          <button onClick={() => navigate("/savkabile/create")} className="create-kabile-btn">
            Kabile Oluştur
          </button>
        </div>
      </div>
    );
  }

  // Backend'den gelen isLeader değerini kullan (highest TP member kontrolü)
  const isLeader = kabile.isLeader ?? (kabile.leaderId === user?.id);
  // Sadece kurucu lider kabile ayarlarını düzenleyebilir
  const isFounder = kabile.founderId === user?.id;
  const memberCount = kabile.members.length;
  const memberPercentage = (memberCount / kabile.maxMembers) * 100;

  return (
    <div className="my-kabile-container">
      <div className="my-kabile-content">
        {/* Header */}
        <header className="kabile-header">
          <button onClick={() => navigate("/savkabile")} className="back-button">
            ← Geri
          </button>
          
          <div className="kabile-title-section">
            {kabile.flagUrl ? (
              <img 
                src={kabile.flagUrl} 
                alt="Kabile Bayrağı" 
                className="kabile-flag"
                onError={(e) => {
                  // Eğer resim yüklenemezse placeholder göster
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="kabile-flag-placeholder">🏛️</div>
            )}
            <div>
              <h1 className="kabile-name">{kabile.name}</h1>
              <span className="kabile-role-badge">
                {isLeader ? "👑 Lider" : "⚔️ Üye"}
              </span>
            </div>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="kabile-stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <div className="stat-label">Üye Sayısı</div>
              <div className="stat-value">{memberCount} / {kabile.maxMembers}</div>
              <div className="stat-progress">
                <div className="stat-progress-bar" style={{ width: `${memberPercentage}%` }}></div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⚔️</div>
            <div className="stat-info">
              <div className="stat-label">Toplam TP</div>
              <div className="stat-value">{kabile.totalTP.toLocaleString()}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-info">
              <div className="stat-label">Min TP Şartı</div>
              <div className="stat-value">{kabile.minTP.toLocaleString()}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-info">
              <div className="stat-label">Kuruluş</div>
              <div className="stat-value">{new Date(kabile.createdAt).toLocaleDateString('tr-TR')}</div>
            </div>
          </div>
        </div>

        {/* Description */}
        {kabile.description && (
          <div className="kabile-description-card">
            <h3>📜 Hakkında</h3>
            <p>{kabile.description}</p>
          </div>
        )}

        {/* Pending Requests (Only for Leaders) */}
        {isLeader && kabile.pendingRequests && kabile.pendingRequests.length > 0 && (
          <div className="kabile-pending-section">
            <div className="section-header">
              <h3>⏳ Bekleyen Talepler ({kabile.pendingRequests.length})</h3>
            </div>
            <div className="pending-requests-list">
              {kabile.pendingRequests.map(request => (
                <div key={request.id} className="pending-request-card">
                  <div className="request-info">
                    {request.avatarUrl ? (
                      <img src={request.avatarUrl} alt={request.username} className="request-avatar" />
                    ) : (
                      <div className="request-avatar-placeholder">
                        {request.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="request-details">
                      <div className="request-name">{request.username}</div>
                      <div className="request-stats">
                        <span>⚔️ {request.savwarTP.toLocaleString()} TP</span>
                        {isErRank(request.savwarRank) ? (
                          <span style={{ fontSize: 20 }}>🟢</span>
                        ) : (
                          <img 
                            src={getRankIcon(request.savwarRank)} 
                            alt="rank" 
                            className="rank-icon"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="request-actions">
                    <button 
                      className="approve-button" 
                      title="Onayla"
                      onClick={() => handleApproveRequest(request.id)}
                    >
                      ✓
                    </button>
                    <button 
                      className="reject-button" 
                      title="Reddet"
                      onClick={() => handleRejectRequest(request.id)}
                    >
                      ✗
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="kabile-members-section">
          <div className="section-header">
            <h3>👥 Üyeler ({memberCount})</h3>
            {isLeader && (
              <button onClick={handleOpenInviteModal} className="invite-button">✉️ Davet Et</button>
            )}
          </div>

          <div className="members-list">
            {kabile.members.map(member => (
              <div key={member.id} className="member-card">
                <div className="member-info">
                  {member.avatarUrl ? (
                    <img src={member.avatarUrl} alt={member.username} className="member-avatar" />
                  ) : (
                    <div className="member-avatar-placeholder">
                      {member.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="member-details">
                    <div className="member-name">
                      {member.username}
                      {member.role === 'leader' && <span className="leader-crown">👑</span>}
                    </div>
                    <div className="member-stats">
                      <span>⚔️ {member.savwarTP.toLocaleString()} TP</span>
                      {isErRank(member.savwarRank) ? (
                        <span style={{ fontSize: 20 }}>🟢</span>
                      ) : (
                        <img 
                          src={getRankIcon(member.savwarRank)} 
                          alt="rank" 
                          className="rank-icon"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="member-actions">
                  {isLeader && member.id !== user?.id && (
                    <button 
                      className="kick-button" 
                      title="Kabileden Çıkar"
                      onClick={() => handleKickMember(member.id, member.username)}
                    >
                      🚫
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="kabile-actions">
          {isFounder ? (
            <>
              <button onClick={handleEdit} className="edit-button">
                ⚙️ Kabile Ayarları
              </button>
              <button onClick={handleDeleteKabile} className="delete-button">
                🗑️ Kabileyi Sil
              </button>
            </>
          ) : (
            <button onClick={handleLeaveKabile} className="leave-button">
              🚪 Kabileden Ayrıl
            </button>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚙️ Kabile Ayarları</h2>
              <button className="modal-close" onClick={() => setShowSettingsModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Kabile Adı</label>
                <input 
                  type="text" 
                  value={kabile?.name || ''} 
                  disabled 
                  className="input-disabled"
                  title="Kabile adı değiştirilemez"
                />
                <small className="form-hint">⚠️ Kabile adı değiştirilemez</small>
              </div>

              <div className="form-group">
                <label>🏳️ Kabile Bayrağı</label>
                <div className="flag-upload-section">
                  {flagPreview ? (
                    <div className="flag-preview-container">
                      <img src={flagPreview} alt="Bayrak önizleme" className="flag-preview" />
                      <button 
                        type="button" 
                        className="change-flag-btn"
                        onClick={() => document.getElementById('flag-input')?.click()}
                      >
                        📷 Bayrağı Değiştir
                      </button>
                    </div>
                  ) : (
                    <div className="flag-upload-placeholder">
                      <div className="upload-icon">🏳️</div>
                      <button 
                        type="button" 
                        className="upload-flag-btn"
                        onClick={() => document.getElementById('flag-input')?.click()}
                      >
                        📤 Bayrak Yükle
                      </button>
                    </div>
                  )}
                  <input
                    id="flag-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFlagUpload}
                    style={{ display: 'none' }}
                  />
                </div>
                <small className="form-hint">En fazla 2MB - PNG, JPG, WebP veya GIF</small>
              </div>

              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={settingsForm.description}
                  onChange={(e) => setSettingsForm({ ...settingsForm, description: e.target.value })}
                  maxLength={200}
                  rows={4}
                  placeholder="Kabile hakkında kısa bir açıklama..."
                />
                <small className="form-hint">{settingsForm.description.length}/200 karakter</small>
              </div>

              <div className="form-group">
                <label>Maksimum Üye Sayısı</label>
                <input
                  type="number"
                  min={kabile?.members.length || 5}
                  max={100}
                  value={settingsForm.maxMembers}
                  onChange={(e) => setSettingsForm({ ...settingsForm, maxMembers: parseInt(e.target.value) || 5 })}
                />
                <small className="form-hint">Mevcut: {kabile?.members.length} üye - En az {kabile?.members.length}, en fazla 100</small>
              </div>

              <div className="form-group">
                <label>Minimum TP Şartı</label>
                <input
                  type="number"
                  min={0}
                  value={settingsForm.minTP}
                  onChange={(e) => setSettingsForm({ ...settingsForm, minTP: parseInt(e.target.value) || 0 })}
                />
                <small className="form-hint">Kabileye katılmak için gereken minimum TP puanı</small>
              </div>

              {settingsError && (
                <div className="error-message">{settingsError}</div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                onClick={() => setShowSettingsModal(false)} 
                className="btn-secondary"
                disabled={isSaving}
              >
                İptal
              </button>
              <button 
                onClick={handleSaveSettings} 
                className="btn-primary"
                disabled={isSaving || isUploadingFlag}
              >
                {isUploadingFlag ? "🏳️ Bayrak yükleniyor..." : isSaving ? "Kaydediliyor..." : "💾 Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {showCropModal && (
        <div className="modal-overlay" onClick={handleCropCancel}>
          <div className="modal-content crop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✂️ Bayrağı Kırp</h2>
              <button className="modal-close" onClick={handleCropCancel}>×</button>
            </div>
            
            <div className="crop-container"
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img src={tempImage} alt="Kırpılacak" className="crop-image" />
              <div
                className="crop-box"
                style={{
                  left: `${cropArea.x}px`,
                  top: `${cropArea.y}px`,
                  width: `${cropArea.width}px`,
                  height: `${cropArea.height}px`,
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onMouseDown={handleMouseDown}
              >
                <div className="crop-handles">
                  <div className="crop-handle top-left"></div>
                  <div className="crop-handle top-right"></div>
                  <div className="crop-handle bottom-left"></div>
                  <div className="crop-handle bottom-right"></div>
                </div>
              </div>
            </div>

            <div className="crop-instructions">
              📌 Kırpma alanını sürükleyerek konumlandırın
            </div>

            <div className="modal-footer">
              <button onClick={handleCropCancel} className="btn-secondary">
                İptal
              </button>
              <button onClick={handleCropComplete} className="btn-primary">
                ✂️ Kırp ve Kullan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content invite-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✉️ Kabileye Davet Et</h2>
              <button className="modal-close" onClick={() => setShowInviteModal(false)}>×</button>
            </div>
            
            {inviteError && (
              <div className="error-message">{inviteError}</div>
            )}

            <div className="invite-content">
              {isLoadingFollowing ? (
                <div className="loading-spinner">Yükleniyor...</div>
              ) : followingUsers.length === 0 ? (
                <div className="no-users-message">
                  <p>Davet edilebilecek kullanıcı yok</p>
                  <small>Takip ettiğiniz ve henüz bir kabileye üye olmayan kullanıcılar burada görünür</small>
                </div>
              ) : (
                <div className="following-list">
                  {followingUsers.map((user) => (
                    <div key={user.id} className="following-item">
                      <div className="user-info">
                        <img
                          src={user.avatarUrlSmall || user.avatarUrl || '/default-avatar.png'}
                          alt={user.username}
                          className="user-avatar"
                        />
                        <div className="user-details">
                          <div className="username">{user.username}</div>
                          <div className="user-tp">⚔️ {user.savwarTP || 0} TP</div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleInviteUser(user.id)}
                        disabled={invitingUserId === user.id}
                        className="invite-btn"
                      >
                        {invitingUserId === user.id ? '⏳' : '✉️ Davet Et'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowInviteModal(false)} className="btn-secondary">
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
